
import Vue from 'vue';
import { HeadlineSettings } from '@root/modules/headlines/types/headlines';
import { HeadlineImage, HeadlinePublishDate, HeadlineTitle } from '@headlines/components/fragments';
import PodcastEmbed from '@root/modules/podcast/components/PodcastEmbed.vue';
import BroadcastTeaser from '@core/components/fragments/FragmentBroadcastTeaser.vue';
import { Article, EmbeddedArticlesElement, EmbeddedArticle } from '@root/modules/article/types/getArticleByID';
import { articleUrl } from '@root/common/filters/urlBuilders';
import { getRootConfig } from '@core/utils/helpers';

interface Props {
  article: Article;
  content: Record<string, any>;
}

interface Computed {
  classModifier: string[];
  isPodcastEmbed: boolean;
  isBroadcastTeaserEmbed: boolean;
  header: string;
  embeddedArticles: EmbeddedArticlesElement | null;
  type: string;
  isArticlesBlockRendered: boolean;
}

interface Data {
  headlines: EmbeddedArticle[];
  headlineSettings: HeadlineSettings;
  observer: IntersectionObserver | null;
}

interface Methods {
  getHeadlines: (data: EmbeddedArticle[]) => EmbeddedArticle[];
  getHeadlineClassModifier: (type: string) => string[];
  getPictureSrcSet: (type: string) => Record<string, any>;
  resort: (items: EmbeddedArticle[], ids: number[]) => EmbeddedArticle[];
  initObserver: () => void;
  destroyObserver: () => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    HeadlineImage,
    HeadlinePublishDate,
    HeadlineTitle,
    PodcastEmbed,
    BroadcastTeaser,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    content: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      headlines: [],
      headlineSettings: {
        publishDate: true,
        titleComment: false,
      },
      observer: null,
    };
  },
  computed: {
    classModifier() {
      if (this.isPodcastEmbed) {
        return ['fragment--podcast-embed'];
      }
      if (this.isBroadcastTeaserEmbed) {
        return ['fragment--broadcast-teaser'];
      }

      const classModifier = ['fragment-related-articles'];

      classModifier.push(`fragment-related-articles--${this.type}`);

      return classModifier;
    },
    isPodcastEmbed() {
      return Boolean(this.embeddedArticles?.items?.length === 1 && this.embeddedArticles?.items[0].primaryCategory.channel.url === 'www.delfi.lt/fone');
    },
    isBroadcastTeaserEmbed() {
      return Boolean(
        this.embeddedArticles?.items?.length === 1 && this.embeddedArticles?.items[0].tags?.items?.find((tag: { id: number }) => tag.id === 96616231)
      );
    },
    embeddedArticles() {
      const { attrs } = this.content || {};
      const relatedArticles = attrs?.relatedArticles || [];
      const embeddedArticles = this.article?.embeddedArticles || [];

      if (!relatedArticles.length || !embeddedArticles.length) return null;

      const orderedIds = relatedArticles.map((r: { id: number }) => r.id);
      const matched = embeddedArticles.find((ea: EmbeddedArticlesElement) => ea.id === attrs.id);

      if (!matched || !Array.isArray(matched.items)) return null;

      return {
        ...matched,
        items: this.resort(matched.items, orderedIds),
      };
    },
    header() {
      return this.$props.content.attrs.text.html;
    },
    type() {
      return this.$props.content.attrs.type;
    },
    isArticlesBlockRendered() {
      return !!(this.headlines.length && !this.isPodcastEmbed && !this.isBroadcastTeaserEmbed);
    },
  },
  watch: {
    isArticlesBlockRendered(value) {
      if (value) {
        this.$nextTick(() => {
          this.initObserver();
        });
      }
    },
  },
  methods: {
    getHeadlines(items) {
      const settings = getRootConfig('settings');
      const domain = settings?.domain;
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;

      const headlines = items.map((headline) => {
        return { ...headline, href: articleUrl({ article: headline as Article, domain, fakeDomainEnabled }) };
      });

      return headlines;
    },
    getHeadlineClassModifier(type) {
      const classModifier: string[] = [];

      classModifier.push(`fragment-related-articles__headline--${type}`);

      switch (type) {
        case 'double':
          classModifier.push('col-6');
          break;
      }

      return classModifier;
    },
    getPictureSrcSet(type) {
      if (type === 'double') {
        return { xs: { w: 384, h: 384, r: '1:1' }, sm: { w: 882, h: 882, r: '1:1' }, lg: { w: 1264, h: 1264, r: '1:1' } };
      }

      return { xs: { w: 384, h: 216 }, sm: { w: 882, h: 496 }, lg: { w: 1264, h: 711 } };
    },
    resort(items: EmbeddedArticle[], ids: number[]) {
      if (!ids.length) {
        return items;
      }

      return items.sort((a: any, b: any) => ids.indexOf(a.id) - ids.indexOf(b.id));
    },
    initObserver() {
      const blockElement = this.$refs[`related-articles-${this.article.id}-${this.type}`] as Element;

      if (!blockElement) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            // Send block impression event
            this.$event?.send('articles-block-impression', { type: `related-articles-${this.type}` });

            if (this.observer) {
              this.observer.disconnect();
              this.observer = null;
            }
          }
        },
        { root: null }
      );

      this.observer.observe(blockElement);
    },
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
  },
  async mounted() {
    if (this.embeddedArticles?.items?.length) {
      this.headlines = this.getHeadlines(this.embeddedArticles.items);

      if (this.isArticlesBlockRendered) {
        this.initObserver();
      }
    }
  },
  beforeDestroy() {
    this.destroyObserver();
  },
});
